import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import {
  Container,
  Budget,
  ActionsContainer,
  CommunicationContainer,
  FiltersContainer,
  RentType,
  modalStyle
} from './styles';
import Modal from '@spa/shared/components/Modal';
import Button from '@spa/shared/components/Button';
import { Input } from '@spa/shared/components/Input';
import Checkbox from '@spa/shared/components/Checkbox';
import { useTracking } from '@spa/shared/hooks/tracking';
import { languageToLocale } from '@spa/shared/utils/language';
import { ResourcesContext } from '@spa/shared/contexts/resources';
import BudgetRange from '@spa/shared/components/Filters/BudgetRange';
import { trackAmplitude } from '@spa/shared/hooks/tracking/services/amplitude/dataAmplitude';
import { ModalContext } from '@spa/shared/contexts/modal';

const CreateOfferAlertModal = ({ onClose }) => {
  const [allowsMarketingCommunications, setAllowsMarketingCommunications] = useState(true);
  const [email, setEmail] = useState('');
  const [canCreateAlert, setCanCreateAlert] = useState(false);

  const {
    t,
    i18n: { language }
  } = useTranslation('translations');
  const { histogram, currency } = useContext(ResourcesContext);
  const { query } = useRouter();
  const { track, events } = useTracking();
  const { state } = useContext(ModalContext);

  const [selectedBudget, setSelectedBudget] = useState(() => ({
    budgetMin: query['budget-min'] || 0,
    budgetMax: query['budget-max'] || 250000
  }));
  const [selectedRentTypes, setSelectedRentTypes] = useState(() => query['rent-type'] || []);

  const termsAndConditionsTranslation = t('create_offer_alert_modal.terms_conditions_description', {
    termsAndConditionsUrl: `${process.env.APP_URL}${language}/terms/terms-of-service`,
    privacyPolicyUrl: `${process.env.APP_URL}${language}/terms/privacy-policy`
  });

  const handleRentTypeChange = ({ target: { name, checked } }) => {
    const newSelectedRentTypes = checked
      ? [...selectedRentTypes, name]
      : selectedRentTypes.filter(type => type !== name);

    setSelectedRentTypes(newSelectedRentTypes);
  };

  const handleBudgetChange = ({ ['budget-min']: budgetMin, ['budget-max']: budgetMax }) =>
    setSelectedBudget({ budgetMin, budgetMax });

  const handleCreateAlert = () => {
    track(events.offerAlertCreated, {
      acceptedTerms: true,
      budgetMin: selectedBudget.budgetMin,
      budgetMax: selectedBudget.budgetMax,
      email,
      rentTypes: selectedRentTypes,
      destinationCity: query.city,
      allowsMarketingCommunications,
      locale: languageToLocale(language)
    });
    trackAmplitude(events.offerAlertCreated, {
      budgetMin: selectedBudget.budgetMin,
      budgetMax: selectedBudget.budgetMax,
      typology: selectedRentTypes,
      destinationCity: query.city,
      source: state.source
    });

    onClose();
  };

  useEffect(() => setCanCreateAlert(!!email), [email]);

  return (
    <Modal style={modalStyle} title={t('create_offer_alert_modal.title')} onClose={onClose}>
      <Container>
        <p className="description">{t('create_offer_alert_modal.description')}</p>

        <FiltersContainer>
          <RentType>
            <p>{t('create_offer_alert_modal.looking_for_label')}</p>
            <Checkbox
              name="property"
              label={t('filters.labels.entireProperty')}
              checked={query['rent-type'] && query['rent-type'].includes('property')}
              onChange={handleRentTypeChange}
            />
            <Checkbox
              name="unit"
              label={t('filters.labels.bedroom')}
              checked={query['rent-type'] && query['rent-type'].includes('unit')}
              onChange={handleRentTypeChange}
            />
            <Checkbox
              name="subunit"
              label={t('filters.labels.sharedBedroom')}
              checked={query['rent-type'] && query['rent-type'].includes('subunit')}
              onChange={handleRentTypeChange}
            />
          </RentType>
          <Budget>
            <p>{t('filters.sections.budget')}</p>
            <BudgetRange
              histogram={histogram}
              onChange={handleBudgetChange}
              start={query['budget-min']}
              end={query['budget-max']}
              startName="budget-min"
              endName="budget-max"
              currencyCode={currency}
              min={0}
              max={250000}
            />
          </Budget>
        </FiltersContainer>

        <CommunicationContainer>
          <Input
            type="email"
            placeholder={t('global.labels.email_address')}
            onChange={({ target: { value } }) => setEmail(value)}
          />
          <Checkbox
            name="communications"
            label={t('create_offer_alert_modal.marketing_communications_label')}
            value="true"
            checked={allowsMarketingCommunications}
            onChange={({ target: { checked } }) => setAllowsMarketingCommunications(checked)}
          />
        </CommunicationContainer>

        <p className="terms-and-conditions" dangerouslySetInnerHTML={{ __html: termsAndConditionsTranslation }} />

        <ActionsContainer>
          <Button isDisabled={!canCreateAlert} onClick={handleCreateAlert}>
            {t('create_offer_alert_modal.create_button_text')}
          </Button>
          <Button className="submit-button--secondary" onClick={onClose}>
            {t('global.labels.cancel')}
          </Button>
        </ActionsContainer>
      </Container>
    </Modal>
  );
};

CreateOfferAlertModal.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default CreateOfferAlertModal;
